import { create } from 'zustand';

interface ScheduleSelectionState {
  /** Currently active (viewed) schedule ID */
  selectedScheduleId: string | null;
  /**
   * Last schedule the detail panel found missing (deleted). Auto-select must never pick it:
   * the sidebar list can still hold the row until its refetch lands (#2416).
   */
  missingScheduleId: string | null;
  /** Checked schedule IDs for bulk operations */
  checkedIds: Set<string>;
  /** Checked folder/group IDs for bulk operations */
  checkedGroupIds: Set<string>;
  /** Whether bulk-selection checkboxes are visible */
  showCheckboxes: boolean;
  selectSchedule: (id: string | null) => void;
  /** Clear the selection because `id` no longer exists, and remember it as missing. */
  clearMissingSchedule: (id: string) => void;
  toggleChecked: (id: string) => void;
  toggleCheckedGroup: (id: string) => void;
  checkAll: (scheduleIds: string[], groupIds?: string[]) => void;
  /** Uncheck specific ids (schedules or folders) — e.g. rows that left the Favorites filter. */
  uncheck: (ids: string[]) => void;
  clearChecked: () => void;
  isChecked: (id: string) => boolean;
  isGroupChecked: (id: string) => boolean;
  setShowCheckboxes: (show: boolean) => void;
}

export const useScheduleSelectionStore = create<ScheduleSelectionState>((set, get) => ({
  selectedScheduleId: null,
  missingScheduleId: null,
  checkedIds: new Set(),
  checkedGroupIds: new Set(),
  showCheckboxes: false,
  selectSchedule: (selectedScheduleId) => set({ selectedScheduleId }),
  clearMissingSchedule: (missingScheduleId) => set({ selectedScheduleId: null, missingScheduleId }),
  toggleChecked: (id) =>
    set((s) => {
      const next = new Set(s.checkedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { checkedIds: next };
    }),
  toggleCheckedGroup: (id) =>
    set((s) => {
      const next = new Set(s.checkedGroupIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { checkedGroupIds: next };
    }),
  checkAll: (scheduleIds, groupIds = []) =>
    set({ checkedIds: new Set(scheduleIds), checkedGroupIds: new Set(groupIds) }),
  uncheck: (ids) =>
    set((s) => {
      if (ids.length === 0) return s;
      const nextIds = new Set(s.checkedIds);
      const nextGroupIds = new Set(s.checkedGroupIds);
      let changed = false;
      for (const id of ids) {
        if (nextIds.delete(id)) changed = true;
        if (nextGroupIds.delete(id)) changed = true;
      }
      return changed ? { checkedIds: nextIds, checkedGroupIds: nextGroupIds } : s;
    }),
  clearChecked: () =>
    set({ checkedIds: new Set(), checkedGroupIds: new Set(), showCheckboxes: false }),
  isChecked: (id) => get().checkedIds.has(id),
  isGroupChecked: (id) => get().checkedGroupIds.has(id),
  setShowCheckboxes: (showCheckboxes) => set({ showCheckboxes }),
}));
